import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Calendar, Check, Pencil, Trash2 } from 'lucide-react';
import { interviewsApi } from '../../services/api';
import UserSelector from '../shared/UserSelector';
import Avatar from '../shared/Avatar';
import EmptyState from '../shared/EmptyState';
import Badge from '../shared/Badge';
import Modal from '../shared/Modal';
import ScheduleInterviewModal from './ScheduleInterviewModal';
import { formatTimeAgo, formatDateRelative } from '../../utils/formatters';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const SUBJECT_OPTIONS = [
  { value: 'primeira_recomendacao', label: 'Primeira Recomendação' },
  { value: 'renovacao_recomendacao', label: 'Renovação de Recomendação' },
  { value: 'selamento', label: 'Selamento' },
  { value: 'avanco_sacerdocio', label: 'Avanço no Sacerdócio' },
  { value: 'chamado', label: 'Chamado' },
  { value: 'ministracao', label: 'Ministração' },
  { value: 'outro', label: 'Outro' },
];

const SUBJECT_BADGE: Record<string, { label: string; variant: 'blue' | 'purple' | 'green' | 'gray' | 'yellow' | 'red' }> = {
  primeira_recomendacao: { label: '1ª Recomend.', variant: 'blue' },
  renovacao_recomendacao: { label: 'Renovação', variant: 'blue' },
  selamento: { label: 'Selamento', variant: 'yellow' },
  avanco_sacerdocio: { label: 'Sacerdócio', variant: 'purple' },
  chamado: { label: 'Chamado', variant: 'purple' },
  ministracao: { label: 'Ministração', variant: 'green' },
  outro: { label: 'Outro', variant: 'gray' },
};

type Tab = 'pendentes' | 'agendadas' | 'concluidas';

const TABS: { key: Tab; label: string }[] = [
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'agendadas', label: 'Agendadas' },
  { key: 'concluidas', label: 'Concluídas' },
];

export default function Interviews() {
  const [tab, setTab] = useState<Tab>('pendentes');
  const [assignedTo, setAssignedTo] = useState('all');
  const [sort, setSort] = useState('oldest');
  const [showNewModal, setShowNewModal] = useState(false);
  const [scheduleInterview, setScheduleInterview] = useState<any>(null);
  const [completeInterview, setCompleteInterview] = useState<any>(null);
  const [editInterview, setEditInterview] = useState<any>(null);
  const [editPendingInterview, setEditPendingInterview] = useState<any>(null);

  const status = tab === 'pendentes' ? 'pending' : tab === 'agendadas' ? 'scheduled' : 'completed';

  const { data, isLoading } = useQuery({
    queryKey: ['interviews', { status, assigned_to: tab !== 'pendentes' ? assignedTo : undefined, sort: tab === 'pendentes' ? sort : 'date' }],
    queryFn: () => interviewsApi.list({
      status,
      ...(tab !== 'pendentes' && { assigned_to: assignedTo }),
      sort: tab === 'pendentes' ? sort : 'date',
    }),
  });

  const interviews = data?.data || [];

  const emptyTitle =
    tab === 'pendentes' ? 'Nenhuma entrevista pendente' :
    tab === 'agendadas' ? 'Nenhuma entrevista agendada' :
    'Nenhuma entrevista concluída';

  const countLabel =
    tab === 'pendentes' ? `${interviews.length} na fila` :
    tab === 'agendadas' ? `${interviews.length} agendada${interviews.length !== 1 ? 's' : ''}` :
    `${interviews.length} concluída${interviews.length !== 1 ? 's' : ''}`;

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'flex-1 py-2 rounded-lg text-sm font-medium transition-colors',
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      {tab === 'pendentes' ? (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">{countLabel}</p>
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600"
          >
            <option value="oldest">Mais antigas</option>
            <option value="newest">Mais recentes</option>
            <option value="subject">Por assunto</option>
          </select>
        </div>
      ) : (
        <div className="mb-4">
          <UserSelector
            value={assignedTo}
            onChange={setAssignedTo}
            includeAll
            filterFn={(u) => !u.role_label?.includes('Secretário')}
          />
          <p className="text-sm text-gray-500 mt-2">{countLabel}</p>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="card animate-pulse h-20" />)}
        </div>
      ) : interviews.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={emptyTitle}
          description={tab === 'pendentes' ? 'Clique em + para adicionar uma pessoa para entrevista' : undefined}
        />
      ) : (
        <div className="space-y-3">
          {interviews.map((interview) => (
            <div key={interview.id} className="card">
              <div className="flex items-start gap-3">
                {tab !== 'pendentes' && interview.assigned_to_user && (
                  <Avatar
                    name={interview.assigned_to_user.name}
                    photoUrl={interview.assigned_to_user.photo_url}
                    size="md"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-gray-800">{interview.person_name}</p>
                    {SUBJECT_BADGE[interview.subject] && (
                      <Badge {...SUBJECT_BADGE[interview.subject]} size="xs" />
                    )}
                  </div>

                  {tab === 'pendentes' ? (
                    <>
                      {interview.subject_detail && (
                        <p className="text-xs text-gray-500 mb-1">{interview.subject_detail}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        Adicionada {formatTimeAgo(interview.created_at)}
                        {interview.created_by_user && ` por ${interview.created_by_user.name.split(' ')[0]}`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        {formatDateRelative(tab === 'agendadas' ? interview.scheduled_date : interview.completed_date)}
                        {tab === 'agendadas' && interview.scheduled_start_time && ` — ${interview.scheduled_start_time}`}
                        {tab === 'agendadas' && interview.scheduled_end_time && ` às ${interview.scheduled_end_time}`}
                      </p>
                      {interview.assigned_to_user && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {interview.assigned_to_user.name.split(' ')[0]} entrevista
                        </p>
                      )}
                      {interview.notes && (
                        <p className="text-xs text-gray-500 mt-1 italic">"{interview.notes}"</p>
                      )}
                    </>
                  )}
                </div>

                {tab === 'pendentes' && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => setScheduleInterview(interview)}
                      className="flex items-center gap-1.5 bg-brand-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-brand-600 transition-colors"
                    >
                      <Calendar size={14} />
                      Agendar
                    </button>
                    <button
                      onClick={() => setEditPendingInterview(interview)}
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
                    >
                      <Pencil size={12} /> Editar
                    </button>
                  </div>
                )}

                {tab === 'agendadas' && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => setEditInterview(interview)}
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
                    >
                      <Pencil size={12} /> Editar
                    </button>
                    <button
                      onClick={() => setCompleteInterview(interview)}
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                    >
                      <Check size={12} /> Concluir
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB — only on Pendentes tab */}
      {tab === 'pendentes' && (
        <button
          onClick={() => setShowNewModal(true)}
          className="fixed bottom-28 right-4 w-14 h-14 bg-brand-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-brand-600 transition-colors z-30"
          aria-label="Nova entrevista"
        >
          <Plus size={24} />
        </button>
      )}

      <NewInterviewModal isOpen={showNewModal} onClose={() => setShowNewModal(false)} />

      {editPendingInterview && (
        <EditPendingInterviewModal
          interview={editPendingInterview}
          onClose={() => setEditPendingInterview(null)}
        />
      )}

      {scheduleInterview && (
        <ScheduleInterviewModal
          interview={scheduleInterview}
          onClose={() => setScheduleInterview(null)}
        />
      )}
      {editInterview && (
        <ScheduleInterviewModal
          interview={editInterview}
          onClose={() => setEditInterview(null)}
        />
      )}
      {completeInterview && (
        <CompleteInterviewModal
          interview={completeInterview}
          onClose={() => setCompleteInterview(null)}
        />
      )}
    </div>
  );
}

function NewInterviewModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [personName, setPersonName] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectDetail, setSubjectDetail] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => interviewsApi.create({
      person_name: personName,
      subject,
      subject_detail: subjectDetail || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Entrevista adicionada!');
      onClose();
      setPersonName('');
      setSubject('');
      setSubjectDetail('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!personName || !subject) return;
    mutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova Entrevista"
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
          <button
            type="submit"
            form="new-interview-form"
            disabled={!personName || !subject || mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Salvando...' : 'Adicionar'}
          </button>
        </div>
      }
    >
      <form id="new-interview-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Nome da pessoa <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={personName}
            onChange={e => setPersonName(e.target.value)}
            className="input"
            placeholder="Nome completo"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="label">Assunto <span className="text-red-500">*</span></label>
          <select value={subject} onChange={e => setSubject(e.target.value)} className="input" required>
            <option value="">Selecione...</option>
            {SUBJECT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Detalhe do assunto (opcional)</label>
          <textarea
            value={subjectDetail}
            onChange={e => setSubjectDetail(e.target.value)}
            className="input resize-none"
            rows={2}
            placeholder="Informações adicionais..."
          />
        </div>
      </form>
    </Modal>
  );
}

function EditPendingInterviewModal({ interview, onClose }: { interview: any; onClose: () => void }) {
  const [personName, setPersonName] = useState(interview.person_name || '');
  const [subject, setSubject] = useState(interview.subject || '');
  const [subjectDetail, setSubjectDetail] = useState(interview.subject_detail || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => interviewsApi.update(interview.id, {
      person_name: personName,
      subject,
      subject_detail: subjectDetail || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Entrevista atualizada!');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => interviewsApi.delete(interview.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Entrevista excluída');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!personName || !subject) return;
    saveMutation.mutate();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Editar entrevista — ${interview.person_name}`}
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
          <button
            type="submit"
            form="edit-pending-form"
            disabled={!personName || !subject || saveMutation.isPending}
            className="btn-primary flex-1"
          >
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      }
    >
      <form id="edit-pending-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Nome da pessoa <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={personName}
            onChange={e => setPersonName(e.target.value)}
            className="input"
            placeholder="Nome completo"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="label">Assunto <span className="text-red-500">*</span></label>
          <select value={subject} onChange={e => setSubject(e.target.value)} className="input" required>
            <option value="">Selecione...</option>
            {SUBJECT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Detalhe do assunto (opcional)</label>
          <textarea
            value={subjectDetail}
            onChange={e => setSubjectDetail(e.target.value)}
            className="input resize-none"
            rows={2}
            placeholder="Informações adicionais..."
          />
        </div>
      </form>

      <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
          >
            <Trash2 size={14} />
            Excluir
          </button>
        ) : (
          <div className="p-3 bg-red-50 rounded-lg space-y-2">
            <p className="text-sm text-red-700 text-center">Confirmar exclusão desta entrevista?</p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 text-sm py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 text-sm py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Excluindo...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function CompleteInterviewModal({ interview, onClose }: { interview: any; onClose: () => void }) {
  const [completedDate, setCompletedDate] = useState(
    interview.scheduled_date || new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState(interview.notes || '');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => interviewsApi.complete(interview.id, {
      completed_date: completedDate,
      notes: notes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Entrevista concluída!');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Concluir Entrevista"
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Concluindo entrevista com <strong>{interview.person_name}</strong>
        </p>
        <div>
          <label className="label">Data da realização</label>
          <input
            type="date"
            value={completedDate}
            onChange={e => setCompletedDate(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label">Observações (opcional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="input resize-none"
            rows={3}
            placeholder="Anotações sobre a entrevista..."
          />
        </div>
      </div>
    </Modal>
  );
}
